import { message } from 'ant-design-vue'
import { http, getRequestBaseUrl } from '@radical/request'
import { Response } from '@/pages/overview/props'
import { getGlobalConfig, useIntervalFn } from '@radical/utils'
import YAML from 'yaml'
const { httpRefreshTime } = getGlobalConfig(import.meta.env)
export function useInterval(cb: Fn): ReturnType<typeof useIntervalFn> {
  return useIntervalFn(cb, httpRefreshTime)
}
export function restartServer(): Promise<any> {
  return http.post({
    url: '/restart',
  })
}

export interface SysInfo {
  arch: string
  cpus: number
  goVersion: string
  localIP: string
  os: string
  plugins: PluginInfo[]
  publicIP: string
  startTime: string
  version: string
}

export interface PluginInfo {
  description: { [key: string]: any }
  name: string
  pushAddr: string[]
  playAddr: string[]
  disabled: boolean
}

/**
 * @description: 版本信息
 */
export function getSysInfo(): Promise<SysInfo> {
  return http.get(
    {
      url: '/sysinfo',
    },
    {
      errorMessageMode: 'message',
    },
  )
}
export function getConfigFile(): Promise<string> {
  return http.get({
    url: '/config/file',
  })
}
export function updateConfigFile(data: string): Promise<any> {
  return http.post({
    url: '/config/file/update',
    data,
  })
}
/**
 * @description: 首页概览
 */
export function getSummary(): Promise<Response> {
  return http.get(
    {
      url: '/summary',
    },
    {
      errorMessageMode: 'message',
    },
  ) as Promise<Response>
}
/**
 * @description: 首页概览
 */
let summarySSE: EventSource | null
export function getSummaryBySSE(
  onmessage: (data: any) => void,
  onError?: (evt: Event) => void,
  onOpen?: (evt: Event) => void,
): () => void {
  summarySSE =
    summarySSE || new EventSource(getRequestBaseUrl() + '/summary/sse')
  try {
    summarySSE.onmessage = (evt) => {
      if (!evt.data) return {}
      onmessage(JSON.parse(evt.data))
    }
    if (onError) summarySSE.onerror = onError
    if (onOpen) summarySSE.onopen = onOpen
  } catch (err) {
    message.error(`Summary EventSource failed: ${err}`)
  }
  return quit
}

function quit(): void {
  summarySSE?.close()
  summarySSE = null
}

export interface TrackSnap {
  writeTime: number
  keyFrame: boolean
  timestamp: number
  sequence: number
  wrap?: { size: number; data?: string }[]
}
export function getTrackSnapSSE(
  streamPath: string,
  type: string,
  onmessage: (data: TrackSnap) => void,
  onError?: (evt: Event) => void,
  onOpen?: (evt: Event) => void,
) {
  let trackSSE: EventSource | null
  trackSSE = new EventSource(
    getRequestBaseUrl() + `/${type}track/sse/${streamPath}`,
  )
  trackSSE.onmessage = (evt) => {
    if (!evt.data) return {}
    onmessage(JSON.parse(evt.data))
  }
  if (onError) trackSSE.onerror = onError
  if (onOpen) trackSSE.onopen = onOpen
  return () => {
    trackSSE?.close()
    trackSSE = null
  }
}
export interface Recording {
  streamPath: string
  filePath: string
  fragment: string
  pluginName: string
  mode: string
  append: boolean
  pointer: string
}
export interface Stream {
  audioTrack: AudioTrack
  bufferTime: string
  gop: number
  isPaused: boolean
  meta: string
  path: string
  pluginName: string
  speed: number
  startTime: string
  state: number
  stopOnIdle: boolean
  subscribers: number
  type: string
  videoTrack: VideoTrack
  recording: Recording[]
}

export interface AudioTrack {
  bps: number
  channels: number
  codec: string
  delta: string
  fps: number
  meta: string
  sampleRate: number
}

export interface VideoTrack {
  bps: number
  codec: string
  delta: string
  fps: number
  gop: number
  height: number
  meta: string
  width: number
}
export function stopStream(streamPath: string): Promise<any> {
  return http.post({
    url: '/stream/stop/' + streamPath,
  })
}
export function getStreamList(): Promise<Stream[]> {
  return http.get({
    url: '/stream/list',
  })
}
/**
 * @description: 流详情
 */
export function getStreamDetail(streamPath: string): Promise<Stream> {
  return http.get({
    url: '/stream/info/' + streamPath,
  })
}
export function getSubscribers(streamPath: string): Promise<any> {
  return http.get({
    url: '/subscribers/' + streamPath,
  })
}
export interface AliasInfo {
  status: 0 | 1 | 2
  alias: string
  autoRemove: boolean
  streamPath: string
}
export function getAliasList(): Promise<AliasInfo[]> {
  return http.get<AliasInfo[]>({
    url: '/stream/alias/list',
  })
}
export interface TransformInfo {
  streamPath: string
  target: string
  pluginName: string
  config: string
}
export function getTransformList(): Promise<TransformInfo[]> {
  return http.get<TransformInfo[]>({
    url: '/transform/list',
  })
}
export interface IRecording {
  streamPath: string
  startTime: string
  type: string
}

export function getRecording() {
  return http.get<IRecording[]>({
    url: '/record/list',
  })
}

export function setStreamAlias(data: Omit<AliasInfo, 'active'>): Promise<any> {
  return http.post({
    url: '/stream/alias',
    data,
  })
}

export function setStreamSpeed(
  streamPath: string,
  speed: number,
): Promise<any> {
  return http.post({
    url: `/stream/speed/${streamPath}`,
    data: { speed },
  })
}

export function seekStream(
  streamPath: string,
  timeStamp: number,
): Promise<any> {
  return http.post({
    url: `/stream/seek/${streamPath}`,
    data: { timeStamp },
  })
}

export interface Step {
  name: string
  description: string
  error: string
  startedAt: string
  completedAt: string
}

export interface SubscriptionProgressData {
  steps: Step[]
  currentStep: number
}

export function getSubscriptionProgress(
  streamPath: string,
): Promise<SubscriptionProgressData> {
  return http.get({
    url: `/stream/progress/${streamPath}`,
  })
}

/**
 * @description: 全局配置
 */
interface IConfigParams {
  name?: string
}
export function getInstanceConfig(
  params: IConfigParams = { name: 'global' },
): Promise<any> {
  const name = params.name || 'global'
  return http.get(
    {
      url: `/config/formily/${name}`,
    },
    {
      errorMessageMode: 'message',
    },
  )
}
export function updateInstanceConfig(
  params: IConfigParams,
  data: any,
): Promise<any> {
  // 将配置对象转换为 YAML 字符串
  const yamlStr = typeof data === 'string' ? data : YAML.stringify(data)
  return http.post(
    {
      url: '/config/modify',
      data: {
        name: params.name || 'global',
        yaml: yamlStr,
      },
    },
    {
      errorMessageMode: 'message',
    },
  )
}
export interface TaskInfo {
  description: {}
  id: number
  type: string
  owner: string
  startTime: string
  state: number
  blocked?: TaskInfo
  blocking?: boolean
  pointer: string
  children: TaskInfo[]
  parent: TaskInfo | null
  parentId: number | null
  eventLoopRunning: boolean
  level: number
}

export function flattenTree(tree: TaskInfo, parent = null) {
  if (!tree) return []
  const firstItem = { ...tree }
  firstItem.children = []
  const flattened = [firstItem]

  function recurse(nodes: TaskInfo[], parent: TaskInfo | null) {
    nodes
      .sort((a, b) => a.id - b.id)
      .forEach((node) => {
        node.parent = parent
        if (parent) {
          node.parentId = parent.id
        } else {
          node.parentId = 0
        }
        if (parent?.blocked && parent.blocked.id === node.id) {
          node.blocking = true
        }
        flattened.push(node)
        if (node.children && node.children.length) {
          recurse(node.children, node)
        }
      })
  }

  recurse(tree.children, parent)
  return flattened
}

export function getTaskTree(): Promise<TaskInfo> {
  return http.get({
    url: '/task/tree',
  })
}

export function restartTask(pointer: string): Promise<any> {
  return http.post({
    url: `/task/restart/${pointer}`,
  })
}

export function stopTask(pointer: string): Promise<any> {
  return http.post({
    url: `/task/stop/${pointer}`,
  })
}

// 存储类型 Schema 定义
export interface StoragePropertyDef {
  type: string
  description: string
  default?: any
  enum?: string[]
}

export interface StorageSchema {
  type: string
  name: string
  description: string
  properties: Record<string, StoragePropertyDef>
}

/**
 * @description: 获取所有已注册的存储类型 Schema
 */
export function getStorageSchemas(): Promise<Record<string, StorageSchema>> {
  return http.get({
    url: '/storage/schemas',
  })
}
